import { CombinedGraphQLErrors } from '@apollo/client/errors';

/**
 * Tells "this API is older than this query" apart from "the request failed".
 *
 * A dashboard build can reach an environment whose API has not caught up yet —
 * dev-api lags the local backend, prod lags dev. GraphQL rejects such a
 * document during validation, before it runs, with `GRAPHQL_VALIDATION_FAILED`.
 * Showing "connection not available" for that is misleading; showing zeros is
 * worse, because zero is a legitimate answer.
 *
 * Apollo Client 4 hands GraphQL errors over as a `CombinedGraphQLErrors` with
 * an `errors` array — the v3 `error.graphQLErrors` shape is gone. Both are read
 * here so this keeps working either side of that upgrade.
 *
 * A validation failure comes back as HTTP 400, and apollo-angular's HttpLink
 * passes that on as Angular's raw `HttpErrorResponse`, with the GraphQL errors
 * in its `error` body — so that shape is read too. Without it, this check never
 * matched an API that was simply older.
 */
export function isUnsupportedByApi(error: unknown): boolean {
  const graphQLErrors = graphQLErrorsOf(error);

  return (
    Array.isArray(graphQLErrors) &&
    graphQLErrors.some(
      (graphQLError) =>
        graphQLError?.extensions?.['code'] === 'GRAPHQL_VALIDATION_FAILED' ||
        /Cannot query field|Unknown type|Unknown argument/i.test(
          graphQLError?.message ?? '',
        ),
    )
  );
}

/** The GraphQL errors in whichever shape Apollo or apollo-angular delivered them. */
export function graphQLErrorsOf(error: unknown): any[] {
  if (CombinedGraphQLErrors.is(error)) return [...error.errors];
  const candidate =
    (error as any)?.graphQLErrors ??
    (error as any)?.errors ??
    // apollo-angular: non-2xx responses arrive as an HttpErrorResponse.
    (error as any)?.error?.errors ??
    [];
  return Array.isArray(candidate) ? candidate : [];
}
